import { useState, useCallback } from 'react';
import { Layout } from '@/components/Layout';
import { useStorage } from '@/hooks/useStorage';
import type { WordEntry, Reward } from '@/lib/index';
import { POINTS_CONFIG } from '@/lib/index';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Trash2, Plus, FileDown, Eye, Sparkles, Lock, Gift, Coins, Wand2, Loader2, Settings } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { PINYIN_INITIALS, HIRAGANA_ROWS, KATAKANA_ROWS, ENGLISH_LETTERS } from '@/data/phonicsData';

const PARENT_PASSWORD = 'youyou';

const COMMON_EMOJIS = [
  '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃',
  '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😙',
  '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯',
  '🍎', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🍑', '🍒', '🥝',
  '⚽', '🏀', '🏈', '⚾', '🎾', '🏐', '🏉', '🎱', '🏓', '🏸',
  '🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '🚐',
  '🎁', '🎈', '🎉', '🎊', '🎀', '🏆', '🥇', '🥈', '🥉', '⭐',
];

const CATEGORIES = [
  { id: 'animals', nameZh: '动物', emoji: '🐼' },
  { id: 'food', nameZh: '食物', emoji: '🍎' },
  { id: 'colors', nameZh: '颜色', emoji: '🌈' },
  { id: 'numbers', nameZh: '数字', emoji: '🔢' },
  { id: 'family', nameZh: '家人', emoji: '👨‍👩‍👧' },
  { id: 'body', nameZh: '身体', emoji: '👋' },
  { id: 'transport', nameZh: '交通', emoji: '🚗' },
  { id: 'weather', nameZh: '天气', emoji: '☀️' },
  { id: 'school', nameZh: '学校', emoji: '🏫' },
  { id: 'daily', nameZh: '日常', emoji: '🏠' },
];

export default function Parent() {
  const { storage, setStorage, setDailyWordCount, setDailyPhonicsCount, getTodayWords } = useStorage();

  const [unlocked, setUnlocked] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const handleUnlock = () => {
    if (passwordInput === PARENT_PASSWORD) {
      setUnlocked(true);
      setPasswordError('');
    } else {
      setPasswordError('密码错误，请重试');
      setPasswordInput('');
    }
  };

  const [isTranslating, setIsTranslating] = useState(false);
  const [formData, setFormData] = useState({
    emoji: '🌟',
    zh: '',
    pinyin: '',
    jp: '',
    kana: '',
    en: '',
    category: 'animals',
  });

  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showPDFPreview, setShowPDFPreview] = useState(false);
  const [previewCanvasData, setPreviewCanvasData] = useState<string>('');

  const [rewardForm, setRewardForm] = useState({
    emoji: '🎁',
    name: '',
    pointsCost: 100,
  });
  const [showRewardEmojiPicker, setShowRewardEmojiPicker] = useState(false);

  const [dailyWordCountLocal, setDailyWordCountLocal] = useState(storage.dailyWordCount ?? 3);
  const [dailyPhonicsCountLocal, setDailyPhonicsCountLocal] = useState(storage.dailyPhonicsCount ?? 10);

  const handleAutoTranslate = useCallback(async () => {
    if (!formData.zh.trim()) {
      toast.error('请先输入中文单词');
      return;
    }
    setIsTranslating(true);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      if (!supabaseUrl || !supabaseKey) throw new Error('No Supabase config');

      const response = await fetch(`${supabaseUrl}/functions/v1/auto-translate-2026`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
          'apikey': supabaseKey,
        },
        body: JSON.stringify({ zh: formData.zh.trim() }),
      });

      if (!response.ok) throw new Error('Translation failed');
      const data = await response.json();

      if (data?.success) {
        setFormData(prev => ({
          ...prev,
          pinyin: (data.pinyin && data.pinyin !== '—') ? data.pinyin : prev.pinyin,
          jp: (data.jp && data.jp !== data.zh && data.jp !== '—') ? data.jp : prev.jp,
          kana: (data.kana && data.kana !== '—') ? data.kana : prev.kana,
          en: (data.en && data.en !== data.zh && data.en !== '—') ? data.en : prev.en,
          emoji: (data.emoji && data.emoji !== '🌟') ? data.emoji : prev.emoji,
        }));
        const isFromDict = data.source === 'dictionary';
        if (isFromDict) {
          toast.success('✨ 自动翻译成功！');
        } else {
          toast.info('💡 在词典中未找到，请手动填写日文和拼音');
        }
      } else {
        throw new Error('No data');
      }
    } catch {
      toast.info('💡 自动翻译不可用，请手动填写日文和英文');
    } finally {
      setIsTranslating(false);
    }
  }, [formData.zh]);

  const handleAddWord = () => {
    if (!formData.zh || !formData.en || !formData.jp) {
      toast.error('请填写必填字段（中文、英文、日文）');
      return;
    }

    const newWord: WordEntry = {
      id: `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      emoji: formData.emoji,
      zh: formData.zh,
      pinyin: formData.pinyin || formData.zh,
      jp: formData.jp,
      kana: formData.kana || formData.jp,
      en: formData.en,
      category: formData.category,
    };

    setStorage((prev) => ({
      ...prev,
      customWords: [...prev.customWords, newWord],
    }));
    toast.success('✨ 单词添加成功！');

    setFormData({
      emoji: '🌟',
      zh: '',
      pinyin: '',
      jp: '',
      kana: '',
      en: '',
      category: 'animals',
    });
  };

  const handleRemoveWord = (wordId: string) => {
    setStorage((prev) => ({
      ...prev,
      customWords: prev.customWords.filter(w => w.id !== wordId),
    }));
    toast.success('单词已删除');
  };

  // Helper: draw a single square grid cell with optional ghost char
  const drawCell = (
    ctx: CanvasRenderingContext2D,
    x: number, y: number, size: number,
    ghostChar: string,
    fontPx: number,
    fontFamily: string,
    showMiziGrid = false
  ) => {
    ctx.strokeStyle = '#c8c8c8';
    ctx.lineWidth = 1;
    ctx.strokeRect(x, y, size, size);
    if (showMiziGrid) {
      ctx.save();
      ctx.strokeStyle = '#e0d0c0';
      ctx.lineWidth = 0.7;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(x + size / 2, y); ctx.lineTo(x + size / 2, y + size);
      ctx.moveTo(x, y + size / 2); ctx.lineTo(x + size, y + size / 2);
      ctx.moveTo(x, y); ctx.lineTo(x + size, y + size);
      ctx.moveTo(x + size, y); ctx.lineTo(x, y + size);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
    }
    if (ghostChar) {
      ctx.save();
      ctx.globalAlpha = 0.13;
      ctx.fillStyle = '#444444';
      ctx.font = `bold ${fontPx}px ${fontFamily}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(ghostChar, x + size / 2, y + size / 2);
      ctx.restore();
    }
  };

  const drawEnglishLines = (
    ctx: CanvasRenderingContext2D,
    x: number, y: number, totalWidth: number,
    ghostText: string
  ) => {
    const ls = 18;
    const lineColors = ['#888', '#bbb', '#bbb', '#888'];
    for (let i = 0; i < 4; i++) {
      ctx.strokeStyle = lineColors[i];
      ctx.lineWidth = i === 0 || i === 3 ? 1 : 0.7;
      ctx.beginPath();
      ctx.moveTo(x, y + i * ls); ctx.lineTo(x + totalWidth, y + i * ls);
      ctx.stroke();
    }
    if (ghostText) {
      ctx.save();
      ctx.globalAlpha = 0.13;
      ctx.fillStyle = '#444';
      ctx.font = `bold ${ls * 1.8}px Arial`;
      ctx.textBaseline = 'alphabetic';
      ctx.textAlign = 'left';
      const charW = ls * 1.5;
      for (let xi = x + 8; xi < x + totalWidth - charW; xi += charW + 4) {
        ctx.fillText(ghostText, xi, y + 3 * ls - 1);
      }
      ctx.restore();
    }
  };

  const generatePDFCanvas = (): string => {
    const todayWords = getTodayWords();
    const dayIndex = (() => {
      const d = new Date();
      const start = new Date(d.getFullYear(), 0, 0);
      return Math.floor((d.getTime() - start.getTime()) / 86400000);
    })();
    const pinyinItem = PINYIN_INITIALS[dayIndex % PINYIN_INITIALS.length];
    const hiraganaRowIndex = dayIndex % HIRAGANA_ROWS.length;
    const hiraganaRow = HIRAGANA_ROWS[hiraganaRowIndex];
    const katakanaRow = KATAKANA_ROWS[hiraganaRowIndex];
    const letterItem = ENGLISH_LETTERS[dayIndex % ENGLISH_LETTERS.length];

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    const PAGE_W = 850;
    const MARGIN = 44;
    const COL_W = PAGE_W - 2 * MARGIN;
    const CELL = 46;
    const GAP = 5;
    const COLS = 10;
    const ROWS = 2;
    const LS = 18; // line spacing for 4-line grid

    // Estimate total height
    const perWordH = (22 + 12 + ROWS * (CELL + GAP) + 14) * 3 + 40;
    const totalH = MARGIN + 70 + todayWords.length * perWordH + 30 + 3 * (4 * LS + 50) + 2 * (ROWS * (CELL + GAP) + 40) + 60;
    canvas.width = PAGE_W;
    canvas.height = Math.max(totalH, 800);

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Header
    ctx.fillStyle = '#2c2c2c';
    ctx.font = 'bold 24px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('宥宥和朋友的"语言家" — 今日书写练习', PAGE_W / 2, MARGIN + 20);
    ctx.font = '14px Arial';
    ctx.fillStyle = '#888';
    ctx.fillText(new Date().toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' }), PAGE_W / 2, MARGIN + 40);

    let yPos = MARGIN + 60;

    // Today's words
    todayWords.forEach((word: WordEntry, wi: number) => {
      ctx.fillStyle = '#2c2c2c';
      ctx.font = 'bold 16px Arial';
      ctx.textAlign = 'left';
      ctx.fillText(`${wi + 1}. ${word.emoji}  ${word.zh}（${word.pinyin || word.zh}）／ ${word.jp}（${word.kana}）／ ${word.en}`, MARGIN, yPos);
      yPos += 20;

      // Chinese mi-character grid (20 boxes, ghost on first)
      ctx.font = '11px Arial';
      ctx.fillStyle = '#777';
      ctx.fillText(`中文 · 米字格（${word.zh}）`, MARGIN, yPos);
      yPos += 10;
      for (let row = 0; row < ROWS; row++) {
        for (let col = 0; col < COLS; col++) {
          const cx = MARGIN + col * (CELL + GAP);
          const cy = yPos + row * (CELL + GAP);
          const isFirst = row === 0 && col === 0;
          drawCell(ctx, cx, cy, CELL, isFirst ? word.zh : '', CELL * 0.76, 'Noto Sans SC, Arial', true);
        }
      }
      yPos += ROWS * (CELL + GAP) + 12;

      // Japanese square grid (20 boxes, kana ghost rotated)
      ctx.font = '11px Arial';
      ctx.fillStyle = '#777';
      ctx.fillText(`日文 · 方格（${word.kana}）`, MARGIN, yPos);
      yPos += 10;
      const kanaChars = word.kana.split('');
      for (let row = 0; row < ROWS; row++) {
        for (let col = 0; col < COLS; col++) {
          const cx = MARGIN + col * (CELL + GAP);
          const cy = yPos + row * (CELL + GAP);
          const charIdx = row * COLS + col;
          drawCell(ctx, cx, cy, CELL, charIdx < kanaChars.length ? kanaChars[charIdx] : '', CELL * 0.76, 'Noto Sans JP, Arial', false);
        }
      }
      yPos += ROWS * (CELL + GAP) + 12;

      // English 4-line grid (2 rows, ghost text)
      ctx.font = '11px Arial';
      ctx.fillStyle = '#777';
      ctx.fillText(`英文 · 四线格（${word.en}）`, MARGIN, yPos);
      yPos += 10;
      drawEnglishLines(ctx, MARGIN, yPos, COL_W, word.en);
      yPos += 4 * LS + 8;
      drawEnglishLines(ctx, MARGIN, yPos, COL_W, '');
      yPos += 4 * LS + 20;
    });

    // Divider
    ctx.strokeStyle = '#ddd';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(MARGIN, yPos); ctx.lineTo(PAGE_W - MARGIN, yPos);
    ctx.stroke();
    yPos += 16;

    // Phonics section
    ctx.fillStyle = '#2c2c2c';
    ctx.font = 'bold 15px Arial';
    ctx.textAlign = 'left';
    ctx.fillText('📚 今日语音练习', MARGIN, yPos);
    yPos += 18;

    // Pinyin 4-line grid
    ctx.font = '12px Arial';
    ctx.fillStyle = '#777';
    ctx.fillText(`拼音 · 四线格（${pinyinItem.symbol}  示例: ${pinyinItem.example}）`, MARGIN, yPos);
    yPos += 10;
    drawEnglishLines(ctx, MARGIN, yPos, COL_W, pinyinItem.symbol);
    yPos += 4 * LS + 8;
    drawEnglishLines(ctx, MARGIN, yPos, COL_W, '');
    yPos += 4 * LS + 16;

    // Hiragana + Katakana grid (10 hira + 10 kata)
    ctx.font = '12px Arial';
    ctx.fillStyle = '#777';
    const kana0 = hiraganaRow.kanas[0]?.char || '';
    const kata0 = katakanaRow?.kanas[0]?.char || '';
    ctx.fillText(`五十音 · 方格（平假名 ${kana0} / 片假名 ${kata0} · ${hiraganaRow.row}行）`, MARGIN, yPos);
    yPos += 10;
    const hiraKanas = hiraganaRow.kanas.map((k: { char: string }) => k.char);
    const kataKanas = katakanaRow?.kanas.map((k: { char: string }) => k.char) || [];
    for (let col = 0; col < COLS; col++) {
      drawCell(ctx, MARGIN + col * (CELL + GAP), yPos, CELL, hiraKanas[col] || '', CELL * 0.76, 'Noto Sans JP, Arial', false);
    }
    yPos += CELL + GAP + 6;
    for (let col = 0; col < COLS; col++) {
      drawCell(ctx, MARGIN + col * (CELL + GAP), yPos, CELL, kataKanas[col] || '', CELL * 0.76, 'Noto Sans JP, Arial', false);
    }
    yPos += CELL + GAP + 16;

    // English letter 4-line grid
    ctx.font = '12px Arial';
    ctx.fillStyle = '#777';
    ctx.fillText(`字母 · 四线格（${letterItem.upper}${letterItem.lower}  示例: ${letterItem.example}）`, MARGIN, yPos);
    yPos += 10;
    drawEnglishLines(ctx, MARGIN, yPos, COL_W, `${letterItem.upper}${letterItem.lower}`);
    yPos += 4 * LS + 8;
    drawEnglishLines(ctx, MARGIN, yPos, COL_W, '');
    yPos += 4 * LS + 18;

    // Footer
    ctx.fillStyle = '#aaa';
    ctx.font = '11px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('宥宥和朋友的"语言家" · The Prophet of Yoyo · © 2026', PAGE_W / 2, yPos + 10);

    return canvas.toDataURL('image/png');
  };

  const handlePreviewPDF = () => {
    const canvasData = generatePDFCanvas();
    setPreviewCanvasData(canvasData);
    setShowPDFPreview(true);
    toast.success('👁 今日书写练习预览已生成');
  };

  const handleDownloadPDF = () => {
    const canvasData = generatePDFCanvas();
    const link = document.createElement('a');
    link.download = `宥宥语言家_书写练习_${new Date().toLocaleDateString('zh-CN')}.png`;
    link.href = canvasData;
    link.click();
    toast.success('📄 书写练习已下载！');
  };




  const handleAddReward = () => {
    if (!rewardForm.name) {
      toast.error('请填写奖励名称');
      return;
    }
    const newReward: Reward = {
      id: `reward_${Date.now()}`,
      name: rewardForm.name,
      emoji: rewardForm.emoji,
      pointsCost: rewardForm.pointsCost,
      createdAt: new Date().toISOString(),
    };
    setStorage((prev) => ({
      ...prev,
      rewards: [...prev.rewards, newReward],
    }));
    toast.success('🎁 奖励添加成功！');
    setRewardForm({ emoji: '🎁', name: '', pointsCost: 100 });
  };

  const handleDeleteReward = (rewardId: string) => {
    setStorage((prev) => ({
      ...prev,
      rewards: prev.rewards.filter(r => r.id !== rewardId),
    }));
    toast.success('奖励已删除');
  };

  const handleSaveSettings = () => {
    setDailyWordCount(dailyWordCountLocal);
    setDailyPhonicsCount(dailyPhonicsCountLocal);
    toast.success('✅ 设置已保存！');
  };

  if (!unlocked) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-background px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-sm"
          >
            <Card className="shadow-xl border-2 border-border">
              <CardHeader className="text-center pb-2">
                <div className="text-6xl mb-2">👨‍👩‍👧</div>
                <CardTitle className="text-2xl font-heading">家长后台</CardTitle>
                <CardDescription>请输入家长密码以继续</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="parent-password">家长密码</Label>
                  <Input
                    id="parent-password"
                    type="password"
                    placeholder="请输入密码"
                    value={passwordInput}
                    onChange={(e) => {
                      setPasswordInput(e.target.value);
                      setPasswordError('');
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && handleUnlock()}
                    className="mt-1 text-lg text-center tracking-widest"
                    autoComplete="off"
                  />
                  {passwordError && (
                    <p className="text-destructive text-sm mt-1 text-center">{passwordError}</p>
                  )}
                </div>
                <Button onClick={handleUnlock} className="w-full" size="lg">
                  <Lock className="w-4 h-4 mr-2" />
                  进入家长后台
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-2">👨‍👩‍👧 家长后台</h1>
            <p className="text-muted-foreground text-lg">管理学习内容，生成练习材料</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={() => setUnlocked(false)}>
              退出后台
            </Button>
          </div>

          <Tabs defaultValue="custom-words" className="w-full">
            <div className="overflow-x-auto">
              <TabsList className="grid w-full grid-cols-4 min-w-max md:min-w-0">
                <TabsTrigger value="custom-words" className="text-xs md:text-sm">
                  <Sparkles className="w-4 h-4 mr-1 md:mr-2" />
                  <span className="hidden sm:inline">词库管理</span>
                  <span className="sm:hidden">词库</span>
                </TabsTrigger>
                <TabsTrigger value="rewards" className="text-xs md:text-sm">
                  <Gift className="w-4 h-4 mr-1 md:mr-2" />
                  <span className="hidden sm:inline">奖励设置</span>
                  <span className="sm:hidden">奖励</span>
                </TabsTrigger>
                <TabsTrigger value="settings" className="text-xs md:text-sm">
                  <Settings className="w-4 h-4 mr-1 md:mr-2" />
                  <span className="hidden sm:inline">学习配置</span>
                  <span className="sm:hidden">配置</span>
                </TabsTrigger>
                <TabsTrigger value="pdf" className="text-xs md:text-sm">
                  <FileDown className="w-4 h-4 mr-1 md:mr-2" />
                  <span className="hidden sm:inline">PDF生成</span>
                  <span className="sm:hidden">PDF</span>
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="custom-words" className="mt-6">
              <div className="grid gap-8 lg:grid-cols-2">
                <Card className="shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Plus className="w-5 h-5" />
                      添加自定义单词
                    </CardTitle>
                    <CardDescription>创建专属于宝宝的学习内容</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>选择表情符号 *</Label>
                      <div className="flex gap-2 mt-2">
                        <Button
                          type="button"
                          variant="outline"
                          className="text-4xl h-16 w-16"
                          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        >
                          {formData.emoji}
                        </Button>
                        <AnimatePresence>
                          {showEmojiPicker && (
                            <motion.div
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.9 }}
                              className="flex-1 grid grid-cols-10 gap-1 p-2 border rounded-lg bg-card max-h-32 overflow-y-auto"
                            >
                              {COMMON_EMOJIS.map((emoji) => (
                                <button
                                  key={emoji}
                                  type="button"
                                  className="text-2xl hover:bg-accent rounded p-1 transition-colors"
                                  onClick={() => {
                                    setFormData({ ...formData, emoji });
                                    setShowEmojiPicker(false);
                                  }}
                                >
                                  {emoji}
                                </button>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-end gap-2">
                        <div className="flex-1">
                          <Label htmlFor="zh">中文单词 *</Label>
                          <Input
                            id="zh"
                            value={formData.zh}
                            onChange={(e) => setFormData({ ...formData, zh: e.target.value })}
                            placeholder="苹果"
                            className="mt-1"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          className="border-2 border-primary text-primary hover:bg-primary/10 gap-1 px-4 h-10 mb-0"
                          onClick={handleAutoTranslate}
                          disabled={isTranslating || !formData.zh.trim()}
                        >
                          {isTranslating ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Wand2 className="w-4 h-4" />
                          )}
                          <span className="hidden sm:inline">{isTranslating ? '翻译中...' : '✨自动填充'}</span>
                          <span className="sm:hidden">✨</span>
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">输入中文后点击「✨自动填充」自动生成日文、英文和图标</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="pinyin">拼音（可选）</Label>
                        <Input
                          id="pinyin"
                          value={formData.pinyin}
                          onChange={(e) => setFormData({ ...formData, pinyin: e.target.value })}
                          placeholder="píng guǒ"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="en">英文 *</Label>
                        <Input
                          id="en"
                          value={formData.en}
                          onChange={(e) => setFormData({ ...formData, en: e.target.value })}
                          placeholder="Apple"
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="jp">日文 *</Label>
                        <Input
                          id="jp"
                          value={formData.jp}
                          onChange={(e) => setFormData({ ...formData, jp: e.target.value })}
                          placeholder="りんご"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="kana">假名（读音）</Label>
                        <Input
                          id="kana"
                          value={formData.kana}
                          onChange={(e) => setFormData({ ...formData, kana: e.target.value })}
                          placeholder="りんご"
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="category">分类</Label>
                      <Select
                        value={formData.category}
                        onValueChange={(value) => setFormData({ ...formData, category: value })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {cat.emoji} {cat.nameZh}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <Button onClick={handleAddWord} className="w-full" size="lg">
                      <Plus className="w-4 h-4 mr-2" />
                      添加单词
                    </Button>
                  </CardContent>
                </Card>

                <Card className="shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5" />
                      自定义单词列表
                    </CardTitle>
                    <CardDescription>
                      已添加 {storage.customWords.length} 个自定义单词
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {storage.customWords.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>还没有自定义单词</p>
                        <p className="text-sm mt-2">在左侧添加专属于宝宝的学习内容吧！</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {storage.customWords.map((word) => (
                          <motion.div
                            key={word.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-3xl">{word.emoji}</span>
                              <div>
                                <div className="font-medium">
                                  {word.zh} / {word.jp} / {word.en}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {word.pinyin} · {word.kana}
                                </div>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveWord(word.id)}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="rewards" className="mt-6">
              <div className="grid gap-8 lg:grid-cols-2">
                <Card className="shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Gift className="w-5 h-5" />
                      添加奖励
                    </CardTitle>
                    <CardDescription>设置孩子可兑换的奖励</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>选择图标</Label>
                      <div className="flex gap-2 mt-2">
                        <Button
                          type="button"
                          variant="outline"
                          className="text-3xl h-12 w-12"
                          onClick={() => setShowRewardEmojiPicker(!showRewardEmojiPicker)}
                        >
                          {rewardForm.emoji}
                        </Button>
                        <AnimatePresence>
                          {showRewardEmojiPicker && (
                            <motion.div
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.9 }}
                              className="flex-1 grid grid-cols-10 gap-1 p-2 border rounded-lg bg-card max-h-32 overflow-y-auto"
                            >
                              {COMMON_EMOJIS.map((emoji) => (
                                <button
                                  key={emoji}
                                  type="button"
                                  className="text-2xl hover:bg-accent rounded p-1 transition-colors"
                                  onClick={() => {
                                    setRewardForm({ ...rewardForm, emoji });
                                    setShowRewardEmojiPicker(false);
                                  }}
                                >
                                  {emoji}
                                </button>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="reward-name">奖励名称 *</Label>
                      <Input
                        id="reward-name"
                        placeholder="例如：看30分钟动画片"
                        value={rewardForm.name}
                        onChange={(e) => setRewardForm({ ...rewardForm, name: e.target.value })}
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="reward-cost">所需积分</Label>
                      <Input
                        id="reward-cost"
                        type="number"
                        min="10"
                        step="10"
                        value={rewardForm.pointsCost}
                        onChange={(e) => setRewardForm({ ...rewardForm, pointsCost: parseInt(e.target.value) || 100 })}
                        className="mt-1"
                      />
                    </div>

                    <Button onClick={handleAddReward} className="w-full" size="lg">
                      <Plus className="w-4 h-4 mr-2" />
                      添加奖励
                    </Button>
                  </CardContent>
                </Card>

                <Card className="shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Gift className="w-5 h-5" />
                      奖励列表
                    </CardTitle>
                    <CardDescription>
                      已设置 {storage.rewards.length} 个奖励
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {storage.rewards.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>还没有设置奖励</p>
                        <p className="text-sm mt-2">在左侧添加孩子喜欢的奖励吧！</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {storage.rewards.map((reward) => (
                          <div
                            key={reward.id}
                            className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-3xl">{reward.emoji}</span>
                              <div>
                                <div className="font-medium">{reward.name}</div>
                                <div className="text-xs text-muted-foreground">
                                  {reward.pointsCost}⚡
                                </div>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteReward(reward.id)}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="settings" className="mt-6">
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    学习配置
                  </CardTitle>
                  <CardDescription>自定义每日学习量</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="daily-word-count">每日学习单词数量</Label>
                    <p className="text-sm text-muted-foreground">设置每天孩子需要学习的新单词数量（1-10个）</p>
                    <div className="flex items-center gap-4">
                      <Input
                        id="daily-word-count"
                        type="number"
                        min="1"
                        max="10"
                        value={dailyWordCountLocal}
                        onChange={(e) => setDailyWordCountLocal(Math.max(1, Math.min(10, parseInt(e.target.value) || 3)))}
                        className="w-24 text-center text-lg font-bold"
                      />
                      <span className="text-muted-foreground">个单词/天</span>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {[1, 2, 3, 5, 7, 10].map(n => (
                        <Button key={n} variant={dailyWordCountLocal === n ? 'default' : 'outline'} size="sm" onClick={() => setDailyWordCountLocal(n)}>
                          {n}个
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="daily-phonics-count">每日发音练习数量</Label>
                    <p className="text-sm text-muted-foreground">设置每天发音练习的数量（5-20组）</p>
                    <div className="flex items-center gap-4">
                      <Input
                        id="daily-phonics-count"
                        type="number"
                        min="5"
                        max="20"
                        value={dailyPhonicsCountLocal}
                        onChange={(e) => setDailyPhonicsCountLocal(Math.max(5, Math.min(20, parseInt(e.target.value) || 10)))}
                        className="w-24 text-center text-lg font-bold"
                      />
                      <span className="text-muted-foreground">组/天</span>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {[5, 10, 15, 20].map(n => (
                        <Button key={n} variant={dailyPhonicsCountLocal === n ? 'default' : 'outline'} size="sm" onClick={() => setDailyPhonicsCountLocal(n)}>
                          {n}组
                        </Button>
                      ))}
                    </div>
                  </div>

                  <Button onClick={handleSaveSettings} className="w-full" size="lg">
                    <Settings className="w-4 h-4 mr-2" />
                    保存设置
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="pdf" className="mt-6">
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileDown className="w-5 h-5" />
                    生成今日书写练习
                  </CardTitle>
                  <CardDescription>
                    根据今日学习单词自动生成描红练习材料（米字格、方格、四线格）
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Today's words preview */}
                  <div className="border rounded-lg p-4 bg-accent/20">
                    <p className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                      <span>📋</span> 今日练习内容（{getTodayWords().length} 个单词）
                    </p>
                    {getTodayWords().length === 0 ? (
                      <p className="text-sm text-muted-foreground">今日还没有解锁单词，请先在「今日学习」中学习单词</p>
                    ) : (
                      <div className="space-y-2">
                        {getTodayWords().map((word) => (
                          <div key={word.id} className="flex items-center gap-3 text-sm">
                            <span className="text-xl">{word.emoji}</span>
                            <span className="font-medium">{word.zh}</span>
                            <span className="text-muted-foreground">/{word.jp}/{word.en}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="mt-3 pt-3 border-t border-border">
                      <p className="text-xs text-muted-foreground">📚 另含：今日拼音、五十音（平/片假名）、英文字母练习</p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={handlePreviewPDF}
                      variant="outline"
                      className="flex-1"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      预览 👁
                    </Button>
                    <Button
                      onClick={handleDownloadPDF}
                      className="flex-1"
                    >
                      <FileDown className="w-4 h-4 mr-2" />
                      下载练习 📄
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

          </Tabs>
        </motion.div>
      </div>

      <Dialog open={showPDFPreview} onOpenChange={setShowPDFPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>PDF 预览</DialogTitle>
            <DialogDescription>
              预览生成的书写练习 PDF
            </DialogDescription>
          </DialogHeader>
          <div className="w-full h-[70vh] border rounded-lg overflow-auto bg-muted/20">
            {previewCanvasData && (
              <img
                src={previewCanvasData}
                alt="PDF Preview"
                className="w-full h-auto"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}